import { createContext } from "react";

import type {
  CurrentAuthState,
  RawSessionData,
  PolicyConfig,
  UserInfo,
  OrgInfo,
} from "./state";
import { UNAUTHENTICATED } from "./state";

// Re-export all state types and symbols
export type {
  CurrentAuthState,
  AuthAction,
  UserInfo,
  OrgInfo,
  Warehouse,
  SessionInfo,
  PolicyConfig,
  Organizations,
  Locations,
} from "./state";

export { toSessionInfo } from "./state";

export {
  Status,
  UNAUTHENTICATED,
  REDIRECTING,
  LOADING_ORGS,
  AWAITING_PASSWORD,
  AUTHENTICATING,
  AWAITING_ORG_SELECT,
  EXCHANGING,
  CREATING_SESSION,
  HYDRATING,
  AUTHENTICATED,
  AUTH_ERROR,
} from "./state";

// ── AuthService interface (implemented by the consuming app) ──

export interface AuthService {
  /**
   * Authenticate via magic link redirect.
   * Exchanges a temporary Stytch token for partial user info (orgs, session).
   *
   * @param params.token - The temporary token from the Stytch redirect URL
   * @param params.tokenType - The Stytch token type (e.g. "discovery")
   * @returns Partial user info including orgs and a Stytch session
   */
  redirect(params: { token: string; tokenType: string }): Promise<UserInfo>;

  /**
   * Look up which organizations a user belongs to, by email address.
   *
   * @param params.email - The user's email address
   * @returns An object containing the user's organizations
   */
  getOrgsForUser(params: { email: string }): Promise<{ orgs?: OrgInfo[] }>;

  /**
   * Authenticate with email and password for a specific org.
   *
   * @param params.email - The user's email address
   * @param params.orgId - The organization to authenticate against
   * @param params.password - The user's password
   * @returns Full user info including JWT token
   */
  authenticateUser(params: {
    email: string;
    orgId: string;
    password: string;
  }): Promise<UserInfo>;

  /**
   * Exchange a Stytch session and org selection for full user info with JWT.
   * Used in the magic link flow after org selection.
   *
   * @param params.orgId - The selected organization
   * @param params.session - The Stytch session from the redirect step
   * @returns Full user info including JWT token
   */
  exchange(params: { orgId: string; session: string }): Promise<UserInfo>;

  /**
   * Mint an AAA session, optionally bound to a warehouse.
   *
   * @param params.locationId - The warehouse to bind the session to, or null
   *   for a session with no warehouse (the product picker needs none)
   * @param params.token - The user JWT to authorize the request
   * @returns Session data including the signed AAA token
   */
  createSession(params: {
    locationId: string | null;
    token: string;
  }): Promise<RawSessionData>;

  /**
   * Refresh user info from an existing token.
   * Used on page reload to rehydrate full user data from a stored session.
   *
   * @param params.token - The stored JWT token
   * @returns Full user info including orgs, roles, and feature flags
   */
  refreshUser(params: { token: string }): Promise<UserInfo>;

  /**
   * Fetch the RBAC policy configuration for the authenticated user.
   *
   * @returns The policy config containing roles and permissions
   */
  getPolicy(): Promise<PolicyConfig>;
}

// ── AuthContext value ──

export type AuthContextValue = {
  state: CurrentAuthState;
  policyConfig: PolicyConfig | null;

  // User intent actions
  startRedirect(token: string, tokenType: string): void;
  startLogin(email: string): void;
  selectOrg(orgId: string): void;
  submitPassword(password: string): void;
  selectLocation(locationId: string): void;
  logout(): void;
};

const defaultValue: AuthContextValue = {
  state: { status: UNAUTHENTICATED },
  policyConfig: null,
  startRedirect: () => {},
  startLogin: () => {},
  selectOrg: () => {},
  submitPassword: () => {},
  selectLocation: () => {},
  logout: () => {},
};

export const AuthContext = createContext<AuthContextValue>(defaultValue);
